/**
 * Scene 枚举与「路由 → Scene」映射。
 *
 * 规范：`docs/engineering/tracking/tracking-conventions.md` §3.3（Scene 取值，两端共享）。
 * KMP 侧对应 `analytics/TrackScene.kt`，**两边必须保持同名同值**。
 */

export const TrackScene = {
  Chat: "Chat",
  Brief: "Brief",
  Plan: "Plan",
  Goal: "Goal",
  Tracker: "Tracker",
  Onboarding: "Onboarding",
  InvestMoves: "InvestMoves",
  Fitness: "Fitness",
  Notification: "Notification",
  Portfolio: "Portfolio",
  Member: "Member",
  My: "My",
  Discover: "Discover",
} as const;

export type TrackSceneValue = (typeof TrackScene)[keyof typeof TrackScene];

/**
 * 路由首段 → Scene。
 *
 * **只映射能确定的。** 行情/资产类路由（`crypto` / `stock` / `etf` / `market` / `news` /
 * `whale-holdings` / `pool` / `themes` / `article` / `edgen-ai` / `alphapick` / `aura` /
 * `task` / `referral` / `search` / `story` / `history` / `extension` / `setting` …）
 * 在现有 Scene 枚举里没有对应项 —— **故意不映射**，`scene` 留空即可（envelope 会省略该字段）。
 *
 * 是否为这些页面扩充 Scene 枚举，属于开发步骤 **Step 6（事件字典规范化）** 的决定，不在这里臆造。
 */
const SEGMENT_TO_SCENE: Record<string, TrackSceneValue> = {
  chat: TrackScene.Chat,
  brief: TrackScene.Brief,
  plan: TrackScene.Plan,
  goal: TrackScene.Goal,
  desk: TrackScene.Tracker,
  invest: TrackScene.InvestMoves,
  discover: TrackScene.Discover,
  notification: TrackScene.Notification,
  portfolio: TrackScene.Portfolio,
  member: TrackScene.Member,
  payment: TrackScene.Member,
  my: TrackScene.My,
  "my-accounts": TrackScene.My,
  onboarding: TrackScene.Onboarding,
  welcome: TrackScene.Onboarding,
  login: TrackScene.Onboarding,
  login_popup: TrackScene.Onboarding,
  sign_in: TrackScene.Onboarding,
};

/** 已知的 locale 段（路由是 `/[locale]/[device]/...`，en 无前缀）。 */
const LOCALES = new Set(["zh", "zh-tw", "ko", "vi", "es", "tr", "fr", "ja"]);
/** 已知的 device 段。 */
const DEVICES = new Set(["desktop", "ios", "android", "h5"]);

/**
 * 从 pathname 取出业务首段（跳过 locale / device 段）。
 * `/zh/h5/plan/checkup` → `plan`；`/chat/abc` → `chat`。
 */
export function businessSegment(pathname: string): string | undefined {
  const parts = pathname.split("/").filter(Boolean);
  let i = 0;
  if (parts[i] && LOCALES.has(parts[i])) {
    i += 1;
  }
  if (parts[i] && DEVICES.has(parts[i])) {
    i += 1;
  }
  return parts[i];
}

/** 路由 → Scene；映射不到就返回 undefined（不猜）。 */
export function sceneForPathname(
  pathname: string,
): TrackSceneValue | undefined {
  const seg = businessSegment(pathname);
  return seg ? SEGMENT_TO_SCENE[seg] : undefined;
}
